import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    LinearProgress,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import ActionButton from './ActionButton.jsx';
import PromoCodeBar from './PromoCodeBar.jsx';
import ProductPreviewArea, { resolvePreviewProps } from './ProductPreviewArea.jsx';
import { PRODUCTS } from '../products';
import { PALETTE, alpha} from '../theme';

/**
 * Inline gallery of orderable products.
 *
 * Each card owns its own caption state; products that expose a `noLabelProductId`
 * also get a "No caption" checkbox which routes the order to a different Zazzle
 * SKU and (if provided) swaps the preview image. When the user clicks Order,
 * the gallery asks the host for the image URL (via `prepareImage`, which
 * usually uploads a canvas/region capture to Firebase Storage) and then hands
 * control back via `onOrder` so the host can run tri-platform tracking and
 * open the Zazzle link.
 */
const ProductGallery = ({
    products = PRODUCTS.filter((p) => !p.hidden),
    defaultLabel = '',
    prepareImage,
    onOrder,
    heading = 'Get it on a product',
    subheading = 'Pick a product, add an optional caption, and we\'ll open Zazzle to finish the order.',
    hideHeader = false,
    hidePromo = false,
    containerSx,
    designSrc,
}) => {
    // `labels` only holds USER-EDITED captions. Anything not in the map falls
    // back to the current `defaultLabel` at render time — that way every card
    // re-derives the caption when the parent's count changes ("0 states
    // visited" → "1 states visited" → …) without us having to push a useEffect
    // sync that would clobber a user who has typed a custom caption.
    // Read screenshot-test overrides from the URL once at mount.
    //   ?nocaption=magnet,button,keychain   → start with those checkboxes ticked
    //   ?caption=                           → keep all captions empty by default
    // No-op in production; only the screenshot test rig sets these flags.
    const [labels, setLabels] = useState(() => {
        if (typeof window === 'undefined') return {};
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.has('caption')) {
                const value = params.get('caption') || '';
                const seeded = {};
                ['cup','tshirt','button','stone-magnet','magnet','keychain','pillow','stone-coaster','beverage-coaster','glass-coaster','acrylic-tray','round-clock','water-bottle','luggage-tag'].forEach(id => { seeded[id] = value; });
                return seeded;
            }
        } catch { /* noop */ }
        return {};
    });
    const [noLabelFlags, setNoLabelFlags] = useState(() => {
        if (typeof window === 'undefined') return {};
        try {
            const params = new URLSearchParams(window.location.search);
            const list = (params.get('nocaption') || '').split(',').map(s => s.trim()).filter(Boolean);
            const seeded = {};
            list.forEach(id => { seeded[id] = true; });
            return seeded;
        } catch { return {}; }
    });
    const [activeProductId, setActiveProductId] = useState(null);
    const [progress, setProgress] = useState(0);

    const labelFor = (productId) =>
        labels[productId] !== undefined ? labels[productId] : defaultLabel;

    const handleLabelChange = (productId, value) => {
        setLabels((prev) => ({ ...prev, [productId]: value }));
    };

    const handleNoLabelToggle = (productId, checked) => {
        setNoLabelFlags((prev) => ({ ...prev, [productId]: checked }));
    };

    const handleOrderClick = useCallback(
        async (product) => {
            if (activeProductId) return;
            const useNoLabel =
                product.supportsLabel && !!noLabelFlags[product.id];
            // If Zazzle has a separate no-label SKU for this product, route to
            // it (and swap the preview image). Otherwise the same template
            // handles both states — we just send an empty caption.
            const effectiveProduct =
                useNoLabel && product.noLabelProductId
                    ? {
                          ...product,
                          zazzleProductId: product.noLabelProductId,
                          image: product.noLabelImage || product.image,
                          analyticsKey: `${product.analyticsKey}_no_label`,
                      }
                    : product;
            const label =
                useNoLabel || !product.supportsLabel
                    ? ''
                    : labelFor(product.id) || '';
            try {
                setActiveProductId(product.id);
                setProgress(0);
                const imageUrl = await prepareImage((pct) => setProgress(pct));
                if (imageUrl) {
                    onOrder({ product: effectiveProduct, label, imageUrl });
                }
            } catch (err) {
                console.error('Order preparation failed:', err);
            } finally {
                setActiveProductId(null);
                setProgress(0);
            }
        },
        // `defaultLabel` participates because labelFor() falls back to it for
        // any product the user hasn't typed a custom caption into — the order
        // must capture the LIVE count, not the count at first mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeProductId, labels, defaultLabel, noLabelFlags, prepareImage, onOrder]
    );

    return (
        <Box sx={{ mt: hideHeader ? 0 : { xs: 3, sm: 4 }, ...containerSx }}>
            {!hideHeader && (
                <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                    <Typography
                        component="h3"
                        sx={{
                            fontSize: { xs: '1.4rem', sm: '1.65rem' },
                            fontWeight: 700,
                            color: PALETTE.text,
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {heading}
                    </Typography>
                    <Typography
                        sx={{
                            color: PALETTE.textBody,
                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                            maxWidth: 520,
                            mx: 'auto',
                            mt: 0.75,
                        }}
                    >
                        {subheading}
                    </Typography>
                </Box>
            )}

            {!hidePromo && <PromoCodeBar />}

            <Typography
                sx={{
                    textAlign: 'center',
                    color: PALETTE.text,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 600,
                    maxWidth: 560,
                    mx: 'auto',
                    mb: { xs: 1, sm: 1.25 },
                    px: 1,
                }}
            >
                Approximate preview — you'll see the exact one on the
                manufacturer's site before checkout.
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',     // 2 cols on mobile
                        sm: 'repeat(3, minmax(0, 1fr))',     // 3 cols on tablet
                        md: 'repeat(4, minmax(0, 1fr))',     // 4 cols on desktop+
                    },
                    gap: { xs: 1.25, sm: 1.5 },
                    maxWidth: 1100,
                    mx: 'auto',
                }}
            >
                {products.map((product) => {
                    const isLoading = activeProductId === product.id;
                    const anyLoading = !!activeProductId;
                    const noLabelChecked = !!noLabelFlags[product.id];
                    // Show the "No caption" checkbox for any product that supports
                    // a caption. When Zazzle has a separate no-label SKU we route
                    // to it; otherwise the same template just renders without text.
                    const canToggleNoLabel = product.supportsLabel;
                    const displayedImage =
                        noLabelChecked && product.noLabelImage
                            ? product.noLabelImage
                            : product.image;
                    const { livePreviewCaption, liveBounds } = resolvePreviewProps(
                        product,
                        { designSrc, caption: labelFor(product.id), noLabel: noLabelChecked },
                    );
                    return (
                        <Box
                            key={product.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: 3,
                                overflow: 'hidden',
                                boxShadow: `0 1px 3px ${alpha('text', 0.04)}`,
                                transition: 'transform 120ms ease, box-shadow 120ms ease',
                                '&:hover': {
                                    transform: anyLoading ? 'none' : 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha('text', 0.08)}`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    backgroundColor: '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: PALETTE.textFaint,
                                }}
                            >
                                <ProductPreviewArea
                                    product={product}
                                    designSrc={designSrc}
                                    livePreviewCaption={livePreviewCaption}
                                    liveBounds={liveBounds}
                                    displayedImage={displayedImage}
                                />
                            </Box>

                            <Box sx={{ p: { xs: 1.75, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontWeight: 700, color: PALETTE.text, mb: 1.25 }}>
                                    {product.name}
                                </Typography>

                                {product.supportsLabel && (
                                    <>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            maxRows={2}
                                            label="Caption"
                                            value={labelFor(product.id)}
                                            onChange={(e) => handleLabelChange(product.id, e.target.value)}
                                            placeholder="e.g. 27 states visited"
                                            disabled={isLoading || noLabelChecked}
                                            sx={{ mb: canToggleNoLabel ? 0.25 : 1.5 }}
                                        />
                                        {canToggleNoLabel && (
                                            <FormControlLabel
                                                sx={{ mb: 1, mt: -0.25 }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={noLabelChecked}
                                                        onChange={(e) =>
                                                            handleNoLabelToggle(product.id, e.target.checked)
                                                        }
                                                        disabled={isLoading}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" sx={{ color: PALETTE.textBody }}>
                                                        No caption
                                                    </Typography>
                                                }
                                            />
                                        )}
                                    </>
                                )}

                                {isLoading && (
                                    <Box sx={{ mb: 1 }}>
                                        <LinearProgress
                                            variant={progress > 0 ? 'determinate' : 'indeterminate'}
                                            value={progress}
                                        />
                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: PALETTE.textMuted }}>
                                            {progress > 0 ? `Uploading… ${progress}%` : 'Preparing…'}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ mt: 'auto' }}>
                                    <ActionButton
                                        variant="dark"
                                        startIcon={<ShoppingCart />}
                                        onClick={() => handleOrderClick(product)}
                                        disabled={anyLoading && !isLoading}
                                        loading={isLoading}
                                        sx={{ width: '100%', minWidth: 0 }}
                                    >
                                        Order
                                    </ActionButton>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

ProductGallery.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    products: PropTypes.array,
    defaultLabel: PropTypes.string,
    prepareImage: PropTypes.func.isRequired,
    onOrder: PropTypes.func.isRequired,
    heading: PropTypes.string,
    subheading: PropTypes.string,
    hideHeader: PropTypes.bool,
    hidePromo: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    containerSx: PropTypes.object,
    designSrc: PropTypes.string,
};

export default ProductGallery;
